// A casca da tabela da aba "Inscritos": lê `rows` já pronta (busca, filtro,
// ordenação e paginação de 8 em 8 — inscritosVocabulary.ts — são a única
// lógica própria deste arquivo) e desenha as colunas por fase. A coluna
// "Ação" é o InscritoRowActions real (menu "⋯" com cadeado justificado);
// antes deste componente existir, a tarefa `inscritos-table` montou esta
// tabela contra um `AcaoCell` provisório (dois botões sem menu de verdade,
// via `onVerRespostas?`/`onCancelar?`/`onOpenMenu?`) — este arquivo
// substitui esse placeholder, não reconstrói a tabela em volta dele.
import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import NoData from '../NoData/NoData';
import InscritoRowActions from './InscritoRowActions';
import { InscritoAvatar } from './InscritoAvatar';
import { InscritoStatusBadge } from './InscritoStatusBadge';
import type { Fase, RegistrationEntry } from './inscritosVocabulary';
import { formatAcertos, formatRegistryDate, formatTempo, latestCancellation, paginateInscritos } from './inscritosVocabulary';

export interface InscritosTableProps {
  fase: Fase;
  rows: RegistrationEntry[];
  /** Repassado ao `InscritoRowActions` de cada linha — botão primário "Cancelar" e a entrada habilitada do menu. */
  onCancelRequest?: (row: RegistrationEntry) => void;
}

/*
  Acima de quantas questões a barra deixa de ser contável e vira proporção.

  ⚠️ A barra segmentada do frame tinha **uma marca fixa por questão**
  (`w-[12px]` + 3px de vão = 15px cada). A largura da célula crescia, então,
  com o tamanho do simulado: 5 questões davam 72px e cabiam na coluna de
  150px — e **todas as stories usam `totalQuestions: 5`**, que foi exatamente
  o que escondeu o defeito até a API entrar no ar. Um simulado real de 50
  questões pede 750px, cinco vezes a coluna, e a célula passava por cima de
  "Tempo" e "Ação".

  Dez é o limite em que as marcas ainda se distinguem dentro da trilha. Acima
  disso a barra vira uma proporção contínua, e não uma listra de marcas
  apertadas: a listra foi tentada e reprovada a olho — com 50 questões ela lê
  como ruído, não como contagem.
*/
const MAX_SEGMENTOS = 10;

function AcertosCell({ row }: { row: RegistrationEntry }) {
  // A linha "não iniciou" do frame troca todo o grupo barra+nota por um
  // único traço neutro em vez de uma barra zerada — quem não respondeu não
  // tem nota nenhuma.
  if (row.answeredCount === 0) {
    return <span className="text-[13px] font-medium text-ink-muted">{formatAcertos(row)}</span>;
  }
  // O frame tinge os segmentos preenchidos com a mesma cor do selo de status
  // da linha (verde em "Concluído", laranja em "Incompleto") em vez de um
  // verde fixo — confirmado segmento a segmento via mcp__pencil em 7 das 8
  // linhas de exemplo do frame; a barra reusa os tokens que o selo ao lado já usa.
  const filledTone = row.derivedStatus === 'incompleto' ? 'bg-warn' : 'bg-ok';
  const contavel = row.totalQuestions > 0 && row.totalQuestions <= MAX_SEGMENTOS;
  const proporcao =
    row.totalQuestions > 0 ? Math.min(100, (row.totalCorrect / row.totalQuestions) * 100) : 0;

  return (
    /*
      `min-w-0` no contêiner e `shrink-0` no número: a trilha é a única parte
      elástica. Quem encolhe é o desenho, nunca o dado — "47/50" truncado para
      "47/5" seria um número errado na tela, e não um número apertado.
    */
    <div className="flex min-w-0 items-center gap-2">
      <div
        className="flex h-[6px] min-w-[24px] flex-1 items-center gap-[3px] overflow-hidden"
        /* A nota ao lado já diz o mesmo, e com mais precisão. */
        aria-hidden
      >
        {contavel ? (
          Array.from({ length: row.totalQuestions }, (_, i) => (
            <span
              key={i}
              /* `flex-1` em vez de `w-[12px]`: as marcas dividem a trilha em
                 vez de a definirem. Com as 5 questões do frame cada uma volta
                 a dar ~12px, então o desenho dele fica igual. */
              className={`h-[6px] flex-1 rounded-[2px] ${
                i < row.totalCorrect ? filledTone : 'bg-surface-muted'
              }`}
            />
          ))
        ) : (
          <div className="h-[6px] w-full rounded-[2px] bg-surface-muted">
            <div
              className={`h-full rounded-[2px] ${filledTone}`}
              style={{ width: `${proporcao}%` }}
            />
          </div>
        )}
      </div>
      <span className="shrink-0 text-[13px] font-bold tabular-nums text-ink">
        {formatAcertos(row)}
      </span>
    </div>
  );
}

export function InscritosTable({ fase, rows, onCancelRequest }: InscritosTableProps) {
  const [page, setPage] = useState(1);
  const { items, from, to, total } = paginateInscritos(rows, page);

  return (
    <>
      <div className="w-full overflow-x-auto rounded-card border border-edge bg-surface">
        <table className="w-full table-fixed text-ink">
          <thead className="bg-surface-muted">
            <tr>
              <th className="h-8 w-[220px] px-3 text-left text-[14px] font-semibold text-ink-secondary">
                Aluno
              </th>
              <th className="h-8 w-[140px] px-3 text-left text-[14px] font-semibold text-ink-secondary">
                Inscrito em
              </th>
              <th className="h-8 w-[130px] px-3 text-left text-[14px] font-semibold text-ink-secondary">Status</th>
              {/*
                `durante` trata as colunas como já existindo, igual a `depois`
                — a prova começou, os números existem (design.md de
                `simulado-cancelamento-efetivo`, Decisão 1) — por isso o corte
                é `fase !== 'antes'`, não uma comparação com `'depois'`.
              */}
              {fase !== 'antes' && (
                <>
                  <th className="h-8 w-[150px] px-3 text-left text-[14px] font-semibold text-ink-secondary">
                    Acertos
                  </th>
                  <th className="h-8 w-[80px] px-3 text-left text-[14px] font-semibold text-ink-secondary">
                    Tempo
                  </th>
                </>
              )}
              <th className="h-8 w-[150px] px-3 text-left text-[14px] font-semibold text-ink-secondary">Ação</th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr key={row.registrationId} className="border-b border-edge last:border-b-0 hover:bg-surface-hover">
                <td className="px-3 py-2">
                  {/*
                    ⚠️ `min-w-0` nos dois níveis, e não é zelo: um filho de
                    flex nasce com `min-width: auto`, então ele se recusa a
                    encolher abaixo do próprio conteúdo e o `truncate` do
                    e-mail **nunca disparava** — com um endereço real a coluna
                    "Aluno" empurrava as vizinhas, do mesmo jeito que a barra
                    de acertos fazia. `title` para o valor inteiro continuar
                    alcançável depois do corte.
                  */}
                  <div className="flex min-w-0 items-center gap-3">
                    <InscritoAvatar name={row.userName} />
                    <div className="flex min-w-0 flex-col">
                      <span
                        className="truncate text-[13px] font-semibold text-ink"
                        title={row.userName}
                      >
                        {row.userName}
                      </span>
                      <span className="truncate text-[12px] text-ink-muted" title={row.userEmail}>
                        {row.userEmail}
                      </span>
                    </div>
                  </div>
                </td>
                <td className="px-3 py-2 text-[12px] font-medium text-ink">
                  {formatRegistryDate(row.registryDate)}
                </td>
                <td className="px-3 py-2">
                  <InscritoStatusBadge
                    derivedStatus={row.derivedStatus}
                    fase={fase}
                    cancellation={latestCancellation(row)}
                  />
                </td>
                {fase !== 'antes' && (
                  <>
                    <td className="px-3 py-2">
                      <AcertosCell row={row} />
                    </td>
                    <td className="px-3 py-2 text-[12px] font-medium text-ink">{formatTempo(row)}</td>
                  </>
                )}
                <td className="px-3 py-2">
                  <InscritoRowActions
                    row={row}
                    fase={fase}
                    onCancelRequest={(cancelledRow) => onCancelRequest?.(cancelledRow)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!items.length && (
        <div className="mt-2 rounded-card border border-edge bg-surface-muted">
          <NoData height={360} />
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[13px] text-ink-muted">
          Mostrando {from}–{to} de {total} inscritos
        </span>
        <div className="flex items-center gap-1.5">
          <button
            type="button"
            aria-label="Página anterior"
            onClick={() => setPage((p) => p - 1)}
            disabled={from <= 1}
            className="flex h-8 w-8 items-center justify-center rounded-input border border-edge-strong text-ink-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronLeft size={16} aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Próxima página"
            onClick={() => setPage((p) => p + 1)}
            disabled={to >= total}
            className="flex h-8 w-8 items-center justify-center rounded-input border border-edge-strong text-ink-secondary hover:bg-surface-hover disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ChevronRight size={16} aria-hidden />
          </button>
        </div>
      </div>
    </>
  );
}

export default InscritosTable;
